// src/lib/apollo.ts

import { ApolloClient, ApolloLink, InMemoryCache, split } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { createGraphQLWSLink } from "./graphqlWSAdapter";

// Environment variables
const API_URL = import.meta.env.VITE_VANDERHEIM_API_URL as string;
const WS_API_URL = import.meta.env.VITE_VANDERHEIM_WS_API_URL as string;

// Build version for client version tracking
const BUILD_VERSION =
    typeof __BUILD_VERSION__ !== "undefined" ? __BUILD_VERSION__ : "dev";

// Export for use in other components
export { BUILD_VERSION };

// Upload-capable HTTP link with headers and response handler
const uploadLink = createUploadLink({
    uri: API_URL,
    credentials: "include",
    headers: {
        "X-Client-Version": BUILD_VERSION,
    },
    fetch: async (uri, options) => {
        const response = await fetch(uri, options);

        // Check for version mismatch in response headers
        const isOutdated = response.headers.get("x-client-outdated");
        const currentVersion = response.headers.get("x-current-version");

        console.debug("[Upload Link] Response object:", response);
        console.debug(
            "[Upload Link] All headers:",
            Array.from(response.headers.entries()),
        );
        console.debug("[Upload Link] Checking version headers:", {
            isOutdated,
            currentVersion,
            BUILD_VERSION,
        });

        if (isOutdated === "true" && currentVersion) {
            console.debug("[Upload Link] Dispatching client-outdated event");
            // Dispatch custom event that useClientVersion hook is listening for
            window.dispatchEvent(
                new CustomEvent("client-outdated", {
                    detail: { version: currentVersion },
                }),
            );
        }

        return response;
    },
});

// Middleware to add CSRF token
const csrfLink = new ApolloLink((operation, forward) => {
    const csrfToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("csrftoken="));

    if (csrfToken) {
        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                "X-CSRFToken": csrfToken.split("=")[1],
            },
        }));
    }

    return forward(operation);
});

// Middleware to ensure optional notification fields are always present
const notificationFieldsLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
        // Add missing optional fields to notificationReceived subscription responses
        if (response.data?.notificationReceived) {
            const notification = response.data.notificationReceived;
            response.data.notificationReceived = {
                imageUrl: null,
                relatedObjectId: null,
                relatedObjectType: null,
                ...notification,
            };
        }
        return response;
    });
});

// WebSocket link for subscriptions
const wsLink = createGraphQLWSLink({
    url: WS_API_URL,
    connectionParams: {
        credentials: "include",
    },
    keepAlive: 30000, // Send ping every 30 seconds to keep connection alive
    retryAttempts: 5,
    shouldRetry: () => true,
    retryWait: async (retries: number) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        const waitTime = Math.min(1000 * Math.pow(2, retries), 16000);
        console.debug(
            `[WebSocket] Retrying connection in ${waitTime}ms (attempt ${retries + 1})`,
        );
        await new Promise((resolve) => setTimeout(resolve, waitTime));
    },
    on: {
        connected: () => console.debug("[WebSocket] Connected successfully"),
        closed: (event: unknown) => {
            const closeEvent = event as CloseEvent;
            console.debug(
                `[WebSocket] Connection closed: code=${closeEvent.code}, reason=${closeEvent.reason}`,
            );
        },
        error: (error: unknown) => {
            console.error("[WebSocket] Connection error:", error);
        },
    },
});

// Use `split` to direct queries/mutations vs subscriptions
const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
        );
    },
    ApolloLink.from([notificationFieldsLink, wsLink]),
    ApolloLink.from([csrfLink, uploadLink]), // Use uploadLink instead of HttpLink here
);

// Initialize Apollo Client
const client = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    // Allow partial query results
                    playerStats: {
                        merge: true,
                    },
                    // Custom merge for MyAccountType to prevent cache data loss warnings
                    // MyAccountType doesn't have an ID field, so we need a custom merge function
                    myAccount: {
                        merge(existing, incoming) {
                            // Deep merge: combine existing and incoming objects
                            // Incoming values override existing ones for specified fields
                            if (!existing) {
                                return incoming;
                            }
                            if (!incoming) {
                                return existing;
                            }
                            return {
                                ...existing,
                                ...incoming,
                                // Ensure nested objects are properly merged
                                streamTeamMember: incoming.streamTeamMember
                                    ? {
                                          ...existing.streamTeamMember,
                                          ...incoming.streamTeamMember,
                                      }
                                    : existing.streamTeamMember,
                                discordAuth: incoming.discordAuth
                                    ? {
                                          ...existing.discordAuth,
                                          ...incoming.discordAuth,
                                      }
                                    : existing.discordAuth,
                                runestones: incoming.runestones
                                    ? incoming.runestones
                                    : existing.runestones,
                                pendingStreamRequest:
                                    incoming.pendingStreamRequest !==
                                    undefined
                                        ? incoming.pendingStreamRequest
                                        : existing.pendingStreamRequest,
                                pendingStreamInvite:
                                    incoming.pendingStreamInvite !==
                                    undefined
                                        ? incoming.pendingStreamInvite
                                        : existing.pendingStreamInvite,
                            };
                        },
                    },
                },
            },
        },
    }),
    // Disable field errors for missing optional fields
    defaultOptions: {
        watchQuery: {
            errorPolicy: "ignore",
        },
        query: {
            errorPolicy: "all",
        },
        mutate: {
            errorPolicy: "all",
        },
    },
});

export default client;
